import { useEffect, useRef, useState } from 'react'

import {
  Drawer,
  Header,
  type InitialUploadFile,
  PrimaryButton,
  SingleFileViewerUpload,
  Space,
  TertiaryButton,
  type UploadFile,
} from '@procurify/ui'
import { useSelector } from 'react-redux'

import { toast } from '@webapp/components/Notification'
import { type Currency } from '@webapp/core/entities/external/Currency'
import { useFormatMedia } from '@webapp/core/hooks/useFormatMedia'
import { makeSelectFeatureSwitches } from '@webapp/core/stores/global'
import { useFeatureFlag, useProcurifyIntl } from '@webapp/hooks'
import { type ExpenseFormReimbursementCustomization } from '@webapp/modules/expense/entities/enums'
import { type ExpenseItem } from '@webapp/modules/expense/entities/ExpenseItem'
import { type RequestMileageConfiguration } from '@webapp/modules/expense/entities/RequestMileageConfiguration'
import messages from './messages'
import { ExpenseLineItemDrawerContentStyled } from './styles/ExpenseLineItemDrawerContentStyled'
import { ExpenseLineItemDrawerDocumentStyled } from './styles/ExpenseLineItemDrawerDocumentStyled'
import { ExpenseLineItemDrawerFooterStyled } from './styles/ExpenseLineItemDrawerFooterStyled'
import { type ExpenseItemFormSchema } from './types'
import { type ExpenseMileageConfiguration } from '../../entities'
import {
  ExpenseLineItemForm,
  type ExpenseLineItemFormHandle,
  type ExpenseLineItemFormSchema,
  mapFormSchemaToExpenseItemFormSchema,
} from '../ExpenseLineItemForm'

export interface IExpenseLineItemDrawerProps {
  drawerOpen: boolean
  onClose: () => void
  onSubmit: (values: ExpenseItemFormSchema) => void
  item?: ExpenseItem
  reimbursementSetting: ExpenseFormReimbursementCustomization
  requesterId: number
  secondaryCategoryId?: number
  baseCurrency?: Nullable<Currency>
  mileageConfiguration?: Nullable<ExpenseMileageConfiguration>
  /**
   * To be removed with PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS
   */
  mileageLockSetting?: RequestMileageConfiguration | null
}

export const ExpenseLineItemDrawer = ({
  drawerOpen,
  onClose,
  onSubmit,
  item,
  reimbursementSetting,
  requesterId,
  secondaryCategoryId,
  baseCurrency,
  mileageLockSetting = null,
  mileageConfiguration = null,
}: IExpenseLineItemDrawerProps) => {
  const intl = useProcurifyIntl()
  const formatMedia = useFormatMedia()
  const PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS = useFeatureFlag(
    'PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS'
  )
  const { reclaimable_expense_mileage_tax } = useSelector(
    makeSelectFeatureSwitches()
  )
  const isReclaimableMileageTaxEnabled =
    PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS && reclaimable_expense_mileage_tax
  const formRef = useRef<ExpenseLineItemFormHandle | null>(null)

  const [uploaderKey, setUploaderKey] = useState(0)

  const initialAttachment = item?.attachment ?? ''
  const [currentAttachment, setCurrentAttachment] =
    useState<string>(initialAttachment)
  const [currentAttachmentMimetype, setCurrentAttachmentMimetype] =
    useState<string>(item?.attachmentMimetype ?? '')

  useEffect(() => {
    setCurrentAttachment(item?.attachment ?? '')
    setCurrentAttachmentMimetype(item?.attachmentMimetype ?? '')
  }, [item])

  const handleUploadComplete = (file: UploadFile) => {
    // Use the server-returned file path ("attachments/<uuid>") so Django
    // FieldFile.url can construct a valid S3 URL on server-side PDF/export
    // paths. Persisting a bare UUID reads back fine in-app (via the
    // /attachments/{uuid}/ resolver) but breaks PO / Remittance rendering.
    // Fail visibly when the response omits .file rather than falling back to
    // the UUID. See PLAT-4061 / PR #9895.
    const filePath = file.rawResponse?.data?.file
    const fileMimetype = file.rawResponse?.data?.mimetype ?? file.mimetype ?? ''
    setCurrentAttachment(filePath ? String(filePath) : '')
    setCurrentAttachmentMimetype(filePath ? String(fileMimetype) : '')
  }

  const handleDeleteFile = () => {
    setCurrentAttachment('')
    setCurrentAttachmentMimetype('')
  }

  const handleFormSubmit = (values: ExpenseLineItemFormSchema) => {
    const mapped = mapFormSchemaToExpenseItemFormSchema(
      values,
      item,
      isReclaimableMileageTaxEnabled
    )
    onSubmit(mapped)
  }

  const initialFile: InitialUploadFile | undefined = currentAttachment
    ? {
        id: currentAttachment,
        file: formatMedia(currentAttachment),
        mimetype: currentAttachmentMimetype,
      }
    : undefined

  const drawerTitle = item
    ? intl.formatMessage(messages.editDrawerTitle)
    : intl.formatMessage(messages.addDrawerTitle)
  const submitButtonText = item
    ? intl.formatMessage(messages.saveButton)
    : intl.formatMessage(messages.createItemButton)

  const renderDocument = () => (
    <ExpenseLineItemDrawerDocumentStyled>
      <SingleFileViewerUpload
        key={uploaderKey}
        initialFile={initialFile}
        onUploadComplete={handleUploadComplete}
        onDelete={handleDeleteFile}
        onItemError={(file) => {
          const errorMessage = file?.rawResponse?.message || ''
          toast.error({
            title: intl.formatMessage(
              messages.fileNotUploadedToastErrorMessage
            ),
            children: errorMessage,
          })
          setUploaderKey((prev) => prev + 1)
        }}
      />
    </ExpenseLineItemDrawerDocumentStyled>
  )

  const handleClose = () => {
    setCurrentAttachment('')
    setCurrentAttachmentMimetype('')
    onClose()
  }

  return (
    <Drawer
      drawerOpen={drawerOpen}
      setDrawerOpen={handleClose}
      minWidth='530px'
      documentOpen={true}
      DocumentProps={{
        renderDocument,
        closeDrawerOnEsc: true,
      }}
      DrawerProps={{
        renderTitle: () => <Header as='h4'>{drawerTitle}</Header>,
        renderFooter: () => (
          <ExpenseLineItemDrawerFooterStyled>
            <Space direction='horizontal' justify='start' align='center'>
              <PrimaryButton
                type='button'
                onClick={() => formRef.current?.submitForm()}
              >
                {submitButtonText}
              </PrimaryButton>
              <TertiaryButton type='button' onClick={handleClose}>
                {intl.formatMessage(messages.cancelButton)}
              </TertiaryButton>
            </Space>
          </ExpenseLineItemDrawerFooterStyled>
        ),
      }}
    >
      {() => (
        <ExpenseLineItemDrawerContentStyled>
          <ExpenseLineItemForm
            key={item?.id ?? 'new'}
            item={item}
            reimbursementSetting={reimbursementSetting}
            secondaryCategoryId={secondaryCategoryId}
            requesterId={requesterId}
            attachment={currentAttachment}
            attachmentMimetype={currentAttachmentMimetype}
            isSubmitting={false}
            onSubmit={handleFormSubmit}
            formRef={formRef}
            baseCurrency={baseCurrency}
            mileageLockSetting={mileageLockSetting}
            mileageConfiguration={mileageConfiguration}
            canOverrideTaxAmount={false}
          />
        </ExpenseLineItemDrawerContentStyled>
      )}
    </Drawer>
  )
}

ExpenseLineItemDrawer.displayName = 'ExpenseLineItemDrawer'
